<template>
  <div class="content">
    <div class="header">
      <h1>{{ pageTitle }}</h1>
    </div>
    <div class="content-body table-wrapper">
      <div class="block">
        <div class="quotes-finder--filter" style="display: flex; flex-flow: row; justify-content: flex-start; gap: 10px;">
          <Input v-model="filter.title" :placeholder="$t('quote.search_from_title_placeholder')" style="flex: 1 1 0px"></Input>
          <Input v-model="filter.number" :placeholder="$t('quote.search_quote_number_placeholder')" style="flex: 1 1 0px"></Input>
          <Select v-model="filter.type" style="flex: 1 1 0px" :placeholder="$t('quote.select_quote_type_placeholder')" clearable>
            <Option value="single_product">{{ $t('quote.types.single_product') }}</Option>
            <Option value="service">{{ $t('quote.types.service') }}</Option>
            <Option value="order_change">{{ $t('quote.types.order_change') }}</Option>
          </Select>
          <ProductSelect v-model="filter.product_id" style="flex: 1 1 0px"></ProductSelect>
          <QuoteMethodSelect v-model="filter.method" style="flex: 1 1 0px" :placeholder="$t('quote.search_quote_method_placeholder')" hide-tip clearable></QuoteMethodSelect>
          <AccountSelect v-model="filter.customer_id" style="flex: 1 1 0px" :placeholder="$t('quote.search_customer_placeholder')" clearable></AccountSelect>
          <PeopleSelect v-model="filter.owner_id" style="flex: 1 1 0px" :placeholder="$t('quote.search_people_placeholder')" clearable></PeopleSelect>
          <QuoteStatusSelect v-model="filter.status" style="flex: 1 1 0px" :placeholder="$t('quote.select_quote_status')" multiple clearable></QuoteStatusSelect>
          <DatePicker type="daterange" :start-date="new Date()" v-model="filter.created_at" placement="bottom-end" placeholder="Select date" style="flex: 1 1 0px" />
          <Button type="primary" @click="getQuotes">{{ $t('quote.search_go') }}</Button>
        </div>
        <div style="margin: 20px 0"></div>
        <vxe-toolbar>
          <template #buttons>
            <div class="text-align-right">
              <Button type="primary" :to="{ name: 'menu.quote.new_quote' }">{{ $t('menu.quote.new_quote') }}</Button>
            </div>
          </template>
        </vxe-toolbar>
        <vxe-table size="mini" :data="quotesList.data" :loading="loading" resizable border>
          <vxe-table-column type="seq" width="40" align="center"></vxe-table-column>
          <vxe-table-column field="number" :title="$t('quote.info.quote_number')" width="200"></vxe-table-column>
          <vxe-table-column field="title" :title="$t('quote.info.title')" width="500"></vxe-table-column>
          <vxe-table-column field="type" :title="$t('quote.info.type')" width="100" align="center">
            <template #default="{ row }">{{ $t(`quote.types.${row.type}`) }}</template>
          </vxe-table-column>
          <vxe-table-column field="data.product.product_name" :title="$t('quote.info.product_name')" width="280"></vxe-table-column>
          <vxe-table-column field="data.quantity" :title="$t('quote.info.quantity')" width="80" align="right"></vxe-table-column>
          <vxe-table-column field="amount" :title="$t('quote.info.amount')" align="right" width="100">
            <template #default="{ row }">{{ intlCurrencyNumber(row.currency, row.amount, 2, 2) }}</template>
          </vxe-table-column>
          <vxe-table-column field="method" :title="$t('quote.info.method')" width="120" align="center">
            <template #default="{ row }"><span :class="{ info: row.method === 'normal' }">{{ row.method ? $t(`quote.methods.${row.method}`) : 'N/A' }}</span></template>
          </vxe-table-column>
          <vxe-table-column field="customer.name" :title="$t('quote.info.customer')" width="200"></vxe-table-column>
          <vxe-table-column field="status" :title="$t('quote.info.status')" width="120" align="center">
            <template #default="{ row }"><span :class="{ warning: row.status === 'open' }">{{ $t(`quote.status.${row.status}`) }}</span></template>
          </vxe-table-column>
          <vxe-table-column :title="$t('quote.info.owner')" width="150">
            <template #default="{ row }">{{ `${row.owner.fullname_cn} (${row.owner.fullname_en})` }}</template>
          </vxe-table-column>
          <vxe-table-column field="created_at" :title="$t('quote.info.created')" align="right" width="110">
            <template #default="{ row }">{{ moment(row.created_at).format('YYYY-MM-DD') }}</template>
          </vxe-table-column>
          <vxe-table-column :title="$t('order.actions')" width="130" align="center">
            <template #default="{ row }">
              <router-link target="_blank" :to="{ name: 'menu.quote.edit', params: { quoteId: row.id } }">{{ $t('edit') }}</router-link>
            </template>
          </vxe-table-column>
        </vxe-table>
        <vxe-pager
            border
            icon-prev-page="fa fa-angle-left"
            icon-jump-prev="fa fa-angle-double-left"
            icon-jump-next="fa fa-angle-double-right"
            icon-next-page="fa fa-angle-right"
            icon-jump-more="fa fa-ellipsis-h"
            :loading="loading"
            :current-page="pager.current_page"
            :page-size="pager.per_page"
            :total="pager.total"
            :layouts="['PrevPage', 'JumpNumber', 'NextPage', 'FullJump', 'Sizes', 'Total']"
            @page-change="handlePageChange">
          </vxe-pager>
      </div>
    </div>
  </div>
</template>

<script>
import { intlCurrencyNumber } from '@/libs/util'
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import { QuoteMethodSelect, QuoteTradeTypeSelect, QuoteStatusSelect } from './components/form-controls'
import { ProductSelect } from '@/view/product/components'
import { PeopleSelect } from '@/view/people/components'
import { AccountSelect } from '@/view/account/components'
export default {
  name: 'Quotes',
  components: {
    QuoteMethodSelect,
    QuoteTradeTypeSelect,
    QuoteStatusSelect,
    PeopleSelect,
    ProductSelect,
    AccountSelect
  },
  data () {
    return {
      loading: false,
      pager: {
        page_size: 15,
        page: 1
      },
      filter: {}
    }
  },
  mounted () {
    this.getQuotes()
  },
  computed: {
    ...mapGetters(['quotesList']),
    pageTitle () {
      document.title = this.$t('menu.quote.quote_list')
      return document.title
    }
  },
  methods: {
    ...mapActions(['loadQuotes']),
    moment,
    intlCurrencyNumber,
    getQuotes () {
      this.loading = true
      this.loadQuotes(this.buildQuery())
        .then(resp => {
          this.pager = (({ per_page, current_page, total }) => ({ per_page, current_page, total }))(resp.data.meta)
        })
        .finally(() => {
          this.loading = false
        })
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.pager.page = currentPage
      this.pager.per_page = pageSize
      this.getQuotes()
    },
    buildQuery () {
      let search = Object.entries(this.filter).filter(([_, v]) => v).map((e) => {
        if (e[0] === 'product_id') {
          e[0] = 'data.product_id'
        }

        if (e[0] === 'created_at') {
          if (e[1][0] && e[1][1]) {
            e[1] = [this.moment(e[1][0]).format('YYYY-MM-DD'), this.moment(e[1][1]).format('YYYY-MM-DD')].join(',')
          } else {
            return
          }
        }
        return e.join(':')
      }).filter(([_, v]) => v).join(';')

      return Object.assign({}, { search: search, searchJoin: 'and' }, this.pager)
    }
  }
}
</script>
